import { useEffect, useState } from "react";
import { Plus, Trash2, Save, X, Edit2, RefreshCw, CheckCircle2, AlertTriangle, KeyRound } from "lucide-react";
import { api, imageUrl } from "@/lib/api";
import ImageUploader from "@/components/admin/ImageUploader";
import { toast } from "sonner";

export default function AdminInstagram() {
    const [tiles, setTiles] = useState([]);
    const [editing, setEditing] = useState(null); // null | "new" | id
    const [form, setForm] = useState({ image_url: "", link_url: "", caption: "", sort_order: 0 });
    const [feedStatus, setFeedStatus] = useState(null);
    const [syncing, setSyncing] = useState(false);

    const load = async () => {
        const { data } = await api.get("/admin/instagram-tiles");
        setTiles(data || []);
    };
    const loadStatus = async () => {
        try {
            const { data } = await api.get("/admin/instagram/feed/status");
            setFeedStatus(data);
        } catch (_) {
            setFeedStatus(null);
        }
    };
    useEffect(() => { load(); loadStatus(); }, []);

    const startNew = () => { setEditing("new"); setForm({ image_url: "", link_url: "", caption: "", sort_order: tiles.length }); };
    const startEdit = (t) => { setEditing(t.id); setForm({ image_url: t.image_url, link_url: t.link_url || "", caption: t.caption || "", sort_order: t.sort_order || 0 }); };
    const cancel = () => setEditing(null);

    const save = async () => {
        if (!form.image_url) { toast.error("Image required"); return; }
        try {
            if (editing === "new") await api.post("/admin/instagram-tiles", form);
            else await api.put(`/admin/instagram-tiles/${editing}`, form);
            toast.success("Saved");
            setEditing(null);
            await load();
        } catch (e) { toast.error("Save failed"); }
    };
    const remove = async (t) => {
        if (!window.confirm("Delete tile?")) return;
        try { await api.delete(`/admin/instagram-tiles/${t.id}`); toast.success("Deleted"); load(); } catch (_) { toast.error("Delete failed"); }
    };

    const syncNow = async () => {
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/instagram/feed/sync");
            if (data?.ok) {
                toast.success(`Synced ${data.posts_count || 0} posts from Instagram`);
            } else {
                toast.error(data?.error || "Sync failed");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Sync failed");
        } finally {
            await loadStatus();
            setSyncing(false);
        }
    };

    return (
        <div className="space-y-5" data-testid="admin-instagram-page">
            <div className="flex items-center justify-between">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Social</div>
                    <h1 className="font-display text-3xl font-black uppercase tracking-tight">Instagram</h1>
                    <p className="text-white/55 text-sm mt-1">
                        Auto-fetch latest posts from Instagram (daily). Manual fallback tiles below if API is unavailable.
                    </p>
                </div>
                <button
                    onClick={syncNow}
                    disabled={syncing}
                    className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-4 py-2.5 rounded-sm hover:bg-[#33C9EA] disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="ig-feed-sync-now"
                >
                    <RefreshCw size={16} className={syncing ? "animate-spin" : ""} />
                    {syncing ? "Syncing…" : "Sync now"}
                </button>
            </div>

            {/* API STATUS CARD */}
            <FeedStatusCard status={feedStatus} />

            {/* MANUAL FALLBACK TILES */}
            <div className="bg-[#121214] border border-[#27272A] rounded-sm">
                <div className="px-4 py-3 border-b border-[#27272A] flex items-center justify-between flex-wrap gap-2">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Manual fallback</div>
                        <div className="text-sm text-white/70 mt-0.5">
                            Shown on the homepage if Instagram API is unavailable.
                        </div>
                    </div>
                    <button onClick={startNew} className="inline-flex items-center gap-2 border border-[#27272A] text-white px-3 py-2 rounded-sm font-display uppercase tracking-widest text-xs font-bold hover:border-[#00BCE5]" data-testid="ig-tile-new">
                        <Plus size={14} /> Add tile
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {editing !== null && (
                        <div className="bg-[#18181B] border border-[#27272A] rounded-sm p-4 space-y-4">
                            <div>
                                <div className="text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1.5">Image</div>
                                <ImageUploader
                                    images={form.image_url ? [form.image_url] : []}
                                    primary={form.image_url}
                                    onChange={(arr) => setForm({ ...form, image_url: arr[arr.length - 1] || "" })}
                                    onPrimaryChange={(u) => setForm({ ...form, image_url: u })}
                                />
                            </div>
                            <div className="grid md:grid-cols-2 gap-3">
                                <Inp label="Link URL (optional)" value={form.link_url} onChange={(v) => setForm({ ...form, link_url: v })} testid="ig-link" />
                                <Inp label="Sort order" type="number" value={form.sort_order} onChange={(v) => setForm({ ...form, sort_order: Number(v) })} testid="ig-sort" />
                            </div>
                            <Inp label="Caption (optional)" value={form.caption} onChange={(v) => setForm({ ...form, caption: v })} testid="ig-caption" />
                            <div className="flex gap-2">
                                <button onClick={save} className="inline-flex items-center gap-2 bg-[#00BCE5] text-black font-display font-bold uppercase tracking-widest px-4 py-2 rounded-sm" data-testid="ig-save">
                                    <Save size={14} /> Save
                                </button>
                                <button onClick={cancel} className="inline-flex items-center gap-2 border border-[#27272A] text-white px-4 py-2 rounded-sm font-display uppercase tracking-widest font-bold">
                                    <X size={14} /> Cancel
                                </button>
                            </div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        {tiles.map((t) => (
                            <div key={t.id} className="border border-[#27272A] rounded-sm bg-[#0A0A0B] overflow-hidden group">
                                <div className="aspect-square bg-[#0A0A0B] overflow-hidden">
                                    <img src={imageUrl(t.image_url)} alt="" className="w-full h-full object-cover" />
                                </div>
                                <div className="p-2 flex justify-between items-center">
                                    <div className="text-[11px] text-white/55">#{t.sort_order || 0}</div>
                                    <div className="inline-flex gap-1">
                                        <button onClick={() => startEdit(t)} className="p-1 text-white/85 hover:text-[#00BCE5]" data-testid={`ig-edit-${t.id}`}>
                                            <Edit2 size={12} />
                                        </button>
                                        <button onClick={() => remove(t)} className="p-1 text-white/85 hover:text-[#FF3B30]" data-testid={`ig-delete-${t.id}`}>
                                            <Trash2 size={12} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {tiles.length === 0 && (
                            <div className="col-span-full text-center text-white/55 p-6 border border-dashed border-[#27272A] rounded-sm">No fallback tiles yet.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function FeedStatusCard({ status }) {
    if (!status) {
        return (
            <div className="bg-[#121214] border border-[#27272A] rounded-sm p-4 text-white/55 text-sm" data-testid="ig-feed-status-card">
                Loading status…
            </div>
        );
    }

    const tokenMissing = !status.token_present || !status.business_id_present;
    let stateLabel, stateColor, StateIcon;
    if (tokenMissing) {
        stateLabel = "Token missing";
        stateColor = "text-amber-400 border-amber-400/40 bg-amber-400/5";
        StateIcon = KeyRound;
    } else if (status.connected) {
        stateLabel = "Connected";
        stateColor = "text-emerald-400 border-emerald-400/40 bg-emerald-400/5";
        StateIcon = CheckCircle2;
    } else {
        stateLabel = "Last sync failed";
        stateColor = "text-red-400 border-red-400/40 bg-red-400/5";
        StateIcon = AlertTriangle;
    }

    const fmt = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
    };

    return (
        <div className="bg-[#121214] border border-[#27272A] rounded-sm p-4 space-y-3" data-testid="ig-feed-status-card">
            <div className="flex flex-wrap items-center gap-3">
                <div className="text-[11px] uppercase tracking-[0.3em] text-[#00BCE5] font-display font-bold">Instagram API</div>
                <span
                    className={`inline-flex items-center gap-1.5 text-xs font-display uppercase tracking-widest font-bold px-2.5 py-1 rounded-sm border ${stateColor}`}
                    data-testid="ig-feed-state-pill"
                >
                    <StateIcon size={12} /> {stateLabel}
                </span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                <Field label="Last synced" value={fmt(status.last_synced_at)} testid="ig-feed-last-synced" />
                <Field label="Posts cached" value={String(status.posts_count ?? 0)} testid="ig-feed-posts-count" />
                <Field label="Fetch limit" value={String(status.fetch_limit ?? 6)} />
                <Field label="API version" value={status.api_version || "—"} />
            </div>

            {status.last_error && (
                <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-sm px-3 py-2" data-testid="ig-feed-last-error">
                    <span className="font-bold">Last error:</span> {status.last_error}
                </div>
            )}

            {tokenMissing && (
                <div className="text-xs text-white/60 bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-2">
                    Configure <code className="text-[#00BCE5]">INSTAGRAM_ACCESS_TOKEN</code> and{" "}
                    <code className="text-[#00BCE5]">INSTAGRAM_BUSINESS_ACCOUNT_ID</code> in backend env to enable the live feed.
                    Manual fallback tiles below will be used until then.
                </div>
            )}
        </div>
    );
}

function Field({ label, value, testid }) {
    return (
        <div className="bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-2">
            <div className="text-[10px] uppercase tracking-widest text-white/45 font-display font-bold">{label}</div>
            <div className="text-white text-sm mt-0.5 truncate" data-testid={testid}>{value}</div>
        </div>
    );
}

function Inp({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-widest font-display font-bold text-white/55 mb-1">{label}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-[#0A0A0B] border border-[#27272A] rounded-sm px-3 py-2 text-white focus:border-[#00BCE5] outline-none" data-testid={testid} />
        </div>
    );
}
